import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { CheckCircle, Calendar, Clock, Scissors, User, MapPin, CalendarPlus, Home, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getCita, getConfiguracion } from "@/lib/api";

export default function ConfirmationPage() {
  const { citaId: id } = useParams();
  const [cita, setCita] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [citaRes, configRes] = await Promise.all([getCita(id), getConfiguracion()]);
        setCita(citaRes.data);
        setConfig(configRes.data);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const getGoogleCalendarUrl = () => {
    if (!cita) return "#";
    const fecha = cita.fecha.replace(/-/g, "");
    const horaInicio = cita.hora_inicio.replace(":", "") + "00";
    const horaFin = cita.hora_fin.replace(":", "") + "00";
    const title = encodeURIComponent(`${cita.servicio_nombre} - ${config?.nombre_negocio || "Barbería"}`);
    const details = encodeURIComponent(`Peluquero: ${cita.peluquero_nombre}\nPrecio: ${cita.precio}€`);
    const location = encodeURIComponent(config?.direccion || "");
    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fecha}T${horaInicio}/${fecha}T${horaFin}&details=${details}&location=${location}&ctz=Europe/Madrid`;
  };

  const getWhatsAppUrl = () => {
    if (!config?.whatsapp) return null;
    const msg = encodeURIComponent(`Hola, acabo de reservar una cita para ${cita?.servicio_nombre} el ${cita ? format(parseISO(cita.fecha), "d 'de' MMMM", { locale: es }) : ""} a las ${cita?.hora_inicio}`);
    return `https://wa.me/${config.whatsapp}?text=${msg}`;
  };

  if (loading) {
    return (<div className="min-h-screen flex items-center justify-center"><div className="spinner w-10 h-10" /></div>);
  }

  if (!cita) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card><CardContent className="p-8 text-center">
          <p className="text-lg mb-4">Cita no encontrada</p>
          <Button asChild><Link to="/">Volver al inicio</Link></Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-12 w-12 text-accent" />
          </div>
          <h1 className="text-3xl font-heading mb-2" data-testid="confirmation-title">Cita confirmada</h1>
          <p className="text-muted-foreground">Tu cita ha sido reservada correctamente</p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><Scissors className="h-6 w-6 text-accent" /></div>
              <div><p className="text-sm text-muted-foreground">Servicio</p><p className="text-lg font-medium" data-testid="cita-servicio">{cita.servicio_nombre}</p></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><Calendar className="h-6 w-6 text-accent" /></div>
              <div><p className="text-sm text-muted-foreground">Fecha</p><p className="text-lg font-medium" data-testid="cita-fecha">{format(parseISO(cita.fecha), "EEEE d 'de' MMMM", { locale: es })}</p></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><Clock className="h-6 w-6 text-accent" /></div>
              <div><p className="text-sm text-muted-foreground">Hora</p><p className="text-lg font-medium" data-testid="cita-hora">{cita.hora_inicio}</p></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><User className="h-6 w-6 text-accent" /></div>
              <div><p className="text-sm text-muted-foreground">Peluquero</p><p className="text-lg font-medium" data-testid="cita-peluquero">{cita.peluquero_nombre}</p></div>
            </div>
            {config?.direccion && (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><MapPin className="h-6 w-6 text-accent" /></div>
                <div><p className="text-sm text-muted-foreground">Direccion</p><p className="text-lg font-medium">{config.direccion}</p></div>
              </div>
            )}
            <div className="border-t pt-4 flex justify-between items-center">
              <span className="text-lg">Total a pagar:</span>
              <span className="text-2xl font-heading text-accent" data-testid="cita-precio">{cita.precio}€</span>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6 bg-muted/50">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Para cancelar esta cita, ve a "Cancelar cita" en la pagina principal e introduce tu nombre y telefono.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Button asChild className="w-full h-14 bg-[#4285f4] hover:bg-[#3367d6] text-white gap-2" data-testid="btn-google-calendar">
            <a href={getGoogleCalendarUrl()} target="_blank" rel="noopener noreferrer"><CalendarPlus className="h-5 w-5" />Añadir a Google Calendar</a>
          </Button>
          {getWhatsAppUrl() && (
            <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-14 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium rounded-md transition-colors" data-testid="btn-whatsapp">
              <MessageCircle className="h-5 w-5" />Contactar por WhatsApp
            </a>
          )}
          <Button asChild variant="outline" className="w-full h-14 gap-2" data-testid="btn-home">
            <Link to="/"><Home className="h-5 w-5" />Volver al inicio</Link>
          </Button>
        </div>

        <div className="text-center mt-8 pb-20 text-sm text-muted-foreground">
          <p>{config?.nombre_negocio || "Barbería"}</p>
          {config?.direccion && <p>{config.direccion}</p>}
        </div>
      </div>
    </div>
  );
}
